import { CircularProgress, styled } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { useState } from "react";
import apiCall from "../../../../utils/apiCall";
import SettingsModal from '../Modal/settingsCustomModal';
import { ReactComponent as RoleIcon } from '../../../../svg/adminRoleIcon.svg';
import { ReactComponent as Arrow } from '../../../../svg/arrow.svg';
import CountryDetails from "./CountryDetails";
import CountryOptions from "./CountryOptions";
import UpdateCountry from "./UpdateCountry";
import DeleteCountry from "./DeleteCountry";

interface Country {
  CountryId: number;
  CountryName: string;
}

interface CountryResponse {
    data: Country[];
}

const CountryContent = () => {
    const [openCountryDetails, setOpenCountryDetails] = useState(false);
    const [openUpdateCountryModal, setOpenUpdateCountryModal] = useState(false);
    const [openDeleteCountryModal, setOpenDeleteCountryModal] = useState(false);
    const [countryName, setCountryName] = useState('');
    const [countryId, setCountryId] = useState(0);

    const handleOpenCountryDetails = () => setOpenCountryDetails(true);
    const handleCloseCountryDetails = () => setOpenCountryDetails(false);

    const handleOpenUpdateCountryModal = () => setOpenUpdateCountryModal(true);
    const handleCloseUpdateCountryModal = () => setOpenUpdateCountryModal(false);

    const handleOpenDeleteCountryModal = () => setOpenDeleteCountryModal(true);
    const handleCloseDeleteCountryModal = () => setOpenDeleteCountryModal(false);

    const handleDelete = () => {
        handleCloseCountryDetails();
        handleOpenDeleteCountryModal();
    };
    
    const handleUpdate = () => {
        handleCloseCountryDetails();
        handleOpenUpdateCountryModal();
    };

    const getCountries = async (): Promise<CountryResponse> => {
        return (await apiCall().get('/Country/GetCountrys')); 
    };

    const { data, isLoading, isError, error } = useQuery(['get-countries'], getCountries, {
        retry: 0,
        keepPreviousData: true,
        enabled: true,
    });

    const getCountryMessage = error as AxiosError;
    const getCountryErrorMessage = getCountryMessage?.message;

    const handleOpen = (country: Country) => {
        setCountryName(country?.CountryName);
        setCountryId(country?.CountryId);
        handleOpenCountryDetails();
    };

    return (
        <>
            {isLoading ? (
                <CircularProgress 
                    sx={{
                        fontSize: '1rem',
                        marginInline: 'auto',
                        display: 'block',
                        marginBlock: '2rem',
                    }}
                />
            ) : isError ? (
                <StyledError>{getCountryErrorMessage}</StyledError>
            ) : (
            <StyledContainer>
                <TotalWrapper>
                    <span>Total</span>
                    <TotalNumberStyle>{data?.data?.length}</TotalNumberStyle>
                </TotalWrapper>
                {data?.data && data?.data?.length > 0 &&
                    data?.data.map((data, index) => (
                    <RoleWrapper onClick={() => handleOpen(data)} key={index}>
                        <div>
                            <RoleIconWrapper><RoleIcon /></RoleIconWrapper>
                            <RoleText>{data?.CountryName}</RoleText>
                            <Active>Active</Active>
                            <DateWrapper></DateWrapper>
                        </div>
                        <ArrowIconWrapper>
                            <Arrow />
                        </ArrowIconWrapper>
                    </RoleWrapper>
                ))}

                <SettingsModal 
                    open={openCountryDetails}
                    cancel={handleCloseCountryDetails}
                    maxWidth='712px'
                    header={countryName}
                    content={<CountryDetails country={countryName} />}
                    card={<CountryOptions 
                        handleUpdate={handleUpdate} 
                        handleDelete={handleDelete}
                    />}
                />

                <SettingsModal
                    open={openUpdateCountryModal}
                    cancel={handleCloseUpdateCountryModal}
                    maxWidth='480px'
                    header='Update Country'
                    showrightIcon={false}
                    content={<UpdateCountry 
                        countryId={countryId} 
                        handleClose={handleCloseUpdateCountryModal} 
                        countryName={countryName}
                    />}   
                />

                <SettingsModal
                    open={openDeleteCountryModal}
                    cancel={handleCloseDeleteCountryModal}
                    maxWidth='653px'
                    header='Delete Country'
                    showrightIcon={false}
                    content={<DeleteCountry 
                        countryId={countryId} 
                        countryName={countryName} 
                        handleClose={handleCloseDeleteCountryModal}
                    />}   
                />
            </StyledContainer>
            )}
        </> 
    )
}

export default CountryContent;

const StyledContainer = styled('div')`
    margin-block: 3.8rem;
    margin-inline: 3.5rem;
`

const TotalNumberStyle = styled('span')`
    background-color: #F1F3F4;
    border-radius: 0.2rem;
    margin-inline-start: 0.4rem;
    padding-inline: 0.2rem;
`

const TotalWrapper = styled('div')`
    color: #4E5A62;
    font-weight: 500;
`

const RoleWrapper = styled('div')`
    display: flex;
    justify-content: space-between;
    border: 0.1rem solid #E1E1E1;
    margin-block: 0.6rem;
    padding-inline: 1.2rem;
    padding-block: 1.2rem;
    border-radius: 0.5rem;
    width: 100%;
    text-align: left;
    background-color: transparent;
`

const RoleIconWrapper = styled('span')`
    background-color: #FFF4DE;
    border-radius: 0.1rem;
    padding-inline: 0.4rem;
`

const RoleText = styled('span')`
    font-weight: 600;
    font-size: 16px;
    margin-inline: 0.8rem;
`

const Active = styled('span')`
    font-size: 14px;
    font-weight: 500;
    background-color: #E0FAEE;
    border-radius: 0.4rem;
    padding-inline: 0.5rem;
    color: #11643C;
`

const DateWrapper = styled('div')`
    margin-block-start: 0.8rem;
`

const ArrowIconWrapper = styled('div')`
    display: flex;
    flex-direction: column;
    justify-content: center;
`

const StyledError = styled('p')`
  color: red;
  font-size: 1.4rem;
  display: flex;
`